/**
 * RUN U — U-3. The AI stream watchdog, as a unit that can be pinned.
 *
 * ~/Desktop/recon-504/04: the hook's watchdog was armed at start() and reset only
 * by SSE events. The intake route answers plain JSON, so nothing reset it; when
 * it fired it hid the progress indicator and did NOT abort the fetch, which ran
 * on until nginx's HTML 504 at 180 s. Nothing reported the fire.
 *
 * A fire now does three things exactly once: abort the request, report it
 * (kind=client_timeout), and let the hook flip its state.
 */

/**
 * SSE routes: 10 s past the 120 s server-side SSE timeout (ddx second opinion,
 * AI narrative), so the server's own `event: timeout` arrives first.
 */
export const CLIENT_WATCHDOG_MS = 130_000;

/**
 * Intake parse (plain JSON, no progress events): 10 s past the route's 150 s
 * deadline (Run U-1) — the same rule as above — and still under nginx's 180 s
 * proxy_read_timeout (recon-504/04). At 130 s the watchdog would always beat
 * the app's honest AI_INTAKE_DEADLINE answer.
 */
export const INTAKE_WATCHDOG_MS = 160_000;

export const CLIENT_TIMEOUT = 'CLIENT_TIMEOUT';
export const PROXY_TIMEOUT = 'PROXY_TIMEOUT';

export interface AiStreamWatchdog {
  /** (Re)arm: any sign of life from the server pushes the deadline out. */
  bump(): void;
  /** Disarm without firing (completion, error, cancel). */
  cancel(): void;
  readonly fired: boolean;
}

export function createAiStreamWatchdog(opts: {
  ms: number;
  abort: () => void;
  report: () => void;
  onFire?: () => void;
}): AiStreamWatchdog {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let fired = false;
  const fire = (): void => {
    if (fired) return;
    fired = true;
    timer = undefined;
    opts.abort();
    opts.report();
    opts.onFire?.();
  };
  return {
    bump() {
      if (fired) return;
      if (timer !== undefined) clearTimeout(timer);
      timer = setTimeout(fire, opts.ms);
    },
    cancel() {
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
    },
    get fired() { return fired; },
  };
}

/**
 * The error for a non-OK response. An app JSON body keeps its own code (e.g.
 * AI_INTAKE_DEADLINE). A proxy's non-JSON 504 becomes PROXY_TIMEOUT, so the
 * panel maps it to honest copy instead of echoing "HTTP 504".
 */
export function nonOkError(status: number, json: { error?: unknown; code?: unknown; [key: string]: unknown } | null): Error & { code?: string; status: number } {
  if (json && (typeof json.code === 'string' || typeof json.error === 'string')) {
    return Object.assign(new Error(typeof json.error === 'string' ? json.error : `Request failed: ${status}`), {
      code: typeof json.code === 'string' ? json.code : undefined,
      status,
    });
  }
  if (status === 504) {
    return Object.assign(new Error('The server did not answer in time.'), { code: PROXY_TIMEOUT, status });
  }
  return Object.assign(new Error(`HTTP ${status}`), { status });
}

const UUID_SEGMENT = /^[0-9a-z]{8}-[0-9a-z]{4}-[0-9a-z]{4}-[0-9a-z]{4}-[0-9a-z]{12}$/i;

/** The route shape of an API path without ids — safe for a log line: `ai/parse-history`. */
export function endpointClass(url: string): string {
  const segs = (url.split('?')[0] ?? '')
    .split('/')
    .filter(Boolean)
    .filter((s) => !UUID_SEGMENT.test(s) && !/^\d+$/.test(s) && !(s.length >= 16 && /\d/.test(s)));
  return segs.slice(-2).join('/') || '-';
}
